import { computed } from 'vue';

import {
  facebookYourPostsMediaCardDisplayMetrics,
  facebookYourPostsSortingOptions as sortingOptions,
} from '@/app/facebook/constants';
import {
  baseQueryParamMapping,
  defaultReportingPeriod,
} from '@/components/core/analytics/queryParamMapping';
import { getMetrics, METRICS } from '@/models/metrics.enum';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { usePlatformStore } from '@/stores/platform';

export const useYourPosts = () => {
  const metrics = getMetrics('yourPosts');
  const platformStore = usePlatformStore();
  const hasConnectedFacebookAdsPreviously = computed(
    () => !platformStore.isPlatformNeverConnected(PLATFORM_CONNECTION.FACEBOOK_ADS_NEW),
  );

  const sortOptions = computed(() => {
    const options = hasConnectedFacebookAdsPreviously.value
      ? [
          { title: 'Organic', children: metrics.all(sortingOptions.organicMetricsAdsConnected) },
          {
            title: 'Organic + Promoted',
            children: metrics.all(sortingOptions.organicAndPromotedMetricsAdsConnected),
          },
          { title: 'Promoted', children: metrics.all(sortingOptions.promotedMetricsAdsConnected) },
          {
            title: 'Discontinued',
            children: metrics.all(sortingOptions.discontinuedMetricsAdsConnected),
          },
        ]
      : [
          { title: 'Organic', children: metrics.all(sortingOptions.organicMetricsUnconnectedAds) },
          {
            title: 'Organic + Promoted',
            children: metrics.all(sortingOptions.organicAndPromotedMetricsUnconnectedAds),
          },
          {
            title: 'Discontinued',
            children: metrics.all(sortingOptions.discontinuedMetricsUnconnectedAds),
          },
        ];

    return [{ label: 'Recently Published', value: 'DATE' }, ...options];
  });

  const banners = computed(() => {
    const bannerConfigs = [];
    if (!hasConnectedFacebookAdsPreviously.value) {
      bannerConfigs.push({
        severity: 'info',
        message:
          'Connect your Meta Ads account for promoted post and ad insights, plus ad comments in Community',
        callToAction: 'Connect',
        onAction: () => platformStore.connect(PLATFORM_CONNECTION.FACEBOOK_ADS_NEW),
      });
    } else if (!platformStore.isPlatformConnected(PLATFORM_CONNECTION.FACEBOOK_ADS_NEW)) {
      bannerConfigs.push({
        severity: 'warn',
        message: 'Meta Ads not connected, data may not be current',
        callToAction: 'Reconnect',
        onAction: () => platformStore.connect(PLATFORM_CONNECTION.FACEBOOK_ADS_NEW),
      });
    }
    return bannerConfigs;
  });

  return {
    title: 'Facebook Posts',
    channelFilterKey: 'facebook',
    filters: {
      brandMediaTypes: ['FACEBOOK_OWNED', 'FACEBOOK_LINK', 'FACEBOOK_TEXT', 'FACEBOOK_TEXT_LINK'],
      default: {
        captionContain: [],
        captionNotContain: [],
        contentTags: [],
        contentTagMatchType: 'ANY',
        postType: [],
        isBoosted: undefined,
        reportingPeriod: defaultReportingPeriod,
        sort: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
      options: {
        isBoosted: [
          { label: 'Organic Posts', value: false },
          { label: 'Promoted Posts', value: true },
        ],
        postType: [
          { label: 'Photo', value: 'PHOTO' },
          { label: 'Video', value: 'VIDEO' },
          { label: 'Reel', value: 'REEL' },
          { label: 'Carousel', value: 'CAROUSEL' },
          { label: 'Text', value: 'TEXT' },
          { label: 'Link', value: 'LINK' },
        ],
      },
      queryParamMapping: {
        ...baseQueryParamMapping,
        isBoosted: { queryParamName: 'isBoosted', type: 'boolean' },
        postType: { queryParamName: 'postType', type: 'array', elementType: 'string' },
        captionContain: {
          queryParamName: 'captionContain',
          type: 'array',
          elementType: 'string',
        },
        captionNotContain: {
          queryParamName: 'captionNotContain',
          type: 'array',
          elementType: 'string',
        },
      },
      scope: 'facebookYourPosts',
      sortDetails: {
        options: sortOptions,
        default: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
    },
    displayMetrics: metrics.all(facebookYourPostsMediaCardDisplayMetrics),
    metricLabels: metrics.all(Object.values(METRICS.FACEBOOK)),
    download: {
      pdf: {
        fileName: 'yourFacebookPostsPDF',
        type: 'facebookYourPosts',
        label: 'Your Facebook Posts PDF',
        pendingKey: 'facebookYourPostsReportPending',
      },
      csv: {
        fileName: 'yourFacebookPostsCSV',
        type: 'facebookYourPosts',
        label: 'Your Facebook Posts CSV',
        pendingKey: 'facebookYourPostsCSVPending',
      },
    },
    platformConnection: PLATFORM_CONNECTION.FACEBOOK_ANALYTICS,
    banners,
  };
};
